import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../config/supabase';

function nrfFiscalYearStart(year) {
  const feb1 = new Date(year, 1, 1);
  const dow = feb1.getDay();
  const offset = dow <= 3 ? -dow : 7 - dow;
  return new Date(year, 1, 1 + offset);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function nrfPeriodEnds(fiscalYearStart, pattern) {
  const dates = [];
  let cursor = new Date(fiscalYearStart);
  for (let q = 0; q < 4; q++) {
    for (let p = 0; p < pattern.length; p++) {
      cursor = addDays(cursor, pattern[p] * 7);
      dates.push(toISODate(addDays(cursor, -1)));
    }
  }
  return dates;
}

function currentNrfFiscalYearStart(today) {
  const year = today.getFullYear();
  const startThisYear = nrfFiscalYearStart(year);
  return today < startThisYear ? nrfFiscalYearStart(year - 1) : startThisYear;
}

function computePeriodEndDates(scheduleType, customDay) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const dates = [];

  if (scheduleType === 'month_end') {
    for (let year = currentYear; year <= currentYear + 1; year++) {
      for (let month = 0; month < 12; month++) {
        const lastDay = new Date(year, month + 1, 0);
        dates.push(toISODate(lastDay));
      }
    }
  } else if (scheduleType === '4-5-4' || scheduleType === '5-4-4') {
    const pattern = scheduleType === '4-5-4' ? [4, 5, 4] : [5, 4, 4];
    const curFyStart = currentNrfFiscalYearStart(today);
    const nextFyStart = nrfFiscalYearStart(curFyStart.getFullYear() + 1);
    nrfPeriodEnds(curFyStart, pattern).forEach((d) => dates.push(d));
    nrfPeriodEnds(nextFyStart, pattern).forEach((d) => dates.push(d));
  } else if (scheduleType === 'custom_day') {
    const clamped = Math.max(1, Math.min(28, parseInt(customDay, 10) || 15));
    for (let year = currentYear; year <= currentYear + 1; year++) {
      for (let month = 0; month < 12; month++) {
        dates.push(toISODate(new Date(year, month, clamped)));
      }
    }
  }

  return [...new Set(dates)].sort();
}

const TYPE_LABELS = {
  month_end: 'Last day of every month',
  '4-5-4': '4-5-4 Accounting Periods',
  '5-4-4': '5-4-4 Accounting Periods',
  custom_day: 'Custom day of month',
  weekly: 'Weekly',
  fiscal: 'Fiscal',
};

function summarizeConfig(cfg) {
  if (!cfg || !cfg.enabled || !cfg.schedule_type) {
    return 'No regular schedule — counted as needed';
  }
  const label = TYPE_LABELS[cfg.schedule_type] || cfg.schedule_type;
  if (cfg.schedule_type === 'custom_day' && cfg.custom_day) {
    return `Day ${cfg.custom_day} of every month`;
  }
  return label;
}

function formatPreviewDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const SCHEDULE_OPTIONS = [
  {
    value: 'month_end',
    icon: '📆',
    title: 'Last day of every month',
    desc: 'Calendar month-end: Jan 31, Feb 28/29, Mar 31, etc.',
  },
  {
    value: '4-5-4',
    icon: '📊',
    title: '4-5-4 Accounting Periods',
    desc: 'NRF fiscal calendar — 4-week, 5-week, 4-week cycles ending Saturdays',
  },
  {
    value: '5-4-4',
    icon: '📊',
    title: '5-4-4 Accounting Periods',
    desc: 'NRF fiscal calendar — 5-week, 4-week, 4-week cycles ending Saturdays',
  },
  {
    value: 'custom_day',
    icon: '🗓️',
    title: 'Custom day of month',
    desc: 'Count on a specific day number each month',
  },
];

const InventoryScheduleEditor = ({
  storeId: storeIdProp,
  onSaved,
  defaultExpanded = false,
  showHeader = true,
}) => {
  const [storeId, setStoreId] = useState(null);
  const [loadedStoreId, setLoadedStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedNotice, setSavedNotice] = useState('');
  const [savedConfig, setSavedConfig] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Draft form state
  const [enabled, setEnabled] = useState(true);
  const [scheduleType, setScheduleType] = useState('month_end');
  const [customDay, setCustomDay] = useState(15);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let sid = storeIdProp;
      if (!sid) {
        const ls = Number(localStorage.getItem('selectedStoreId'));
        if (Number.isFinite(ls) && ls > 0) sid = ls;
      }
      if (!sid) {
        if (!cancelled) {
          setError('No store selected.');
          setLoading(false);
        }
        return;
      }
      // Already loaded this store — skip to avoid clobbering in-progress edits
      // when the parent re-renders with the same effective storeId (e.g.
      // EOPWizard transitions storeIdProp from null → number after its own
      // useEffect runs, and the localStorage fallback resolved to the same id).
      if (sid === loadedStoreId) {
        if (!cancelled) setStoreId(sid);
        return;
      }
      if (!cancelled) {
        setLoading(true);
        setError('');
        setStoreId(sid);
      }
      try {
        const { data, error: loadErr } = await supabase
          .from('stores')
          .select('inventory_period_config')
          .eq('id', sid)
          .maybeSingle();
        if (loadErr) throw loadErr;
        const cfg = data?.inventory_period_config || null;
        if (!cancelled) {
          setSavedConfig(cfg);
          setLoadedStoreId(sid);
          if (cfg) {
            setEnabled(cfg.enabled !== false && !!cfg.schedule_type);
            setScheduleType(cfg.schedule_type || 'month_end');
            setCustomDay(cfg.custom_day || 15);
          } else {
            setEnabled(false);
            setScheduleType('month_end');
            setCustomDay(15);
          }
          setExpanded(defaultExpanded);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load schedule.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storeIdProp]);

  const previewDates = useMemo(() => {
    if (!enabled || !scheduleType) return [];
    const all = computePeriodEndDates(scheduleType, customDay);
    const todayIso = toISODate(new Date());
    const upcoming = all.filter((d) => d >= todayIso);
    return upcoming.slice(0, 6);
  }, [enabled, scheduleType, customDay]);

  const handleSave = async () => {
    if (!storeId) {
      setError('No store selected.');
      return;
    }
    setSaving(true);
    setError('');
    setSavedNotice('');
    try {
      let newConfig;
      if (!enabled) {
        newConfig = {
          enabled: false,
          schedule_type: null,
          custom_day: null,
          period_end_dates: [],
        };
      } else {
        const clampedCustom = scheduleType === 'custom_day'
          ? Math.max(1, Math.min(28, parseInt(customDay, 10) || 15))
          : null;
        newConfig = {
          enabled: true,
          schedule_type: scheduleType,
          custom_day: clampedCustom,
          period_end_dates: computePeriodEndDates(scheduleType, clampedCustom ?? customDay),
        };
      }

      // Verify the row was actually updated. Under RLS or with a stale
      // storeId the .update() can silently affect zero rows without throwing —
      // requesting .select().single() back forces a real "not found / not
      // permitted" error so we don't lie to the user about saving.
      const { data: updated, error: updErr } = await supabase
        .from('stores')
        .update({ inventory_period_config: newConfig })
        .eq('id', storeId)
        .select('id, inventory_period_config')
        .single();
      if (updErr) throw updErr;
      if (!updated) {
        throw new Error("Couldn't save: the schedule wasn't written. Check that you're signed in to the right store.");
      }

      const persisted = updated.inventory_period_config || newConfig;
      setSavedConfig(persisted);
      setLoadedStoreId(storeId);
      setSavedNotice('Schedule saved.');
      setExpanded(false);
      if (typeof onSaved === 'function') onSaved(persisted);
      setTimeout(() => setSavedNotice(''), 4000);
    } catch (e) {
      setError(e.message || 'Failed to save schedule.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert draft to last saved
    setError('');
    if (savedConfig) {
      setEnabled(savedConfig.enabled !== false && !!savedConfig.schedule_type);
      setScheduleType(savedConfig.schedule_type || 'month_end');
      setCustomDay(savedConfig.custom_day || 15);
    } else {
      setEnabled(false);
      setScheduleType('month_end');
      setCustomDay(15);
    }
    setExpanded(false);
  };

  if (loading) {
    return (
      <div style={{
        padding: '1rem 1.25rem',
        background: '#FBF6F1',
        border: '1px solid #E8DDD0',
        borderRadius: 12,
        color: '#8B7B73',
        fontSize: '0.9rem',
      }}>
        Loading schedule…
      </div>
    );
  }

  const summary = summarizeConfig(savedConfig);

  return (
    <div style={{
      background: '#FEFCFA',
      border: '1px solid #E8DDD0',
      borderRadius: 12,
      padding: '1rem 1.25rem',
    }}>
      {showHeader && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              color: '#3D2518',
              fontSize: '1rem',
              marginBottom: 2,
            }}>
              📆 End of Period Schedule
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6c625a' }}>
              {summary}
            </div>
          </div>
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                background: '#fff',
                border: '1px solid #C9A66B',
                color: '#7A5C2E',
                borderRadius: 8,
                padding: '0.4rem 0.9rem',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      )}

      {savedNotice && !expanded && (
        <div style={{
          marginTop: '0.6rem',
          padding: '0.5rem 0.75rem',
          background: '#EEF6EE',
          border: '1px solid #B8E0BC',
          borderRadius: 8,
          color: '#2D6A31',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}>
          ✓ {savedNotice}
        </div>
      )}

      {expanded && (
        <div style={{ marginTop: showHeader ? '1rem' : 0 }}>
          <p style={{
            fontSize: '0.85rem',
            color: '#5A4A42',
            marginBottom: '0.85rem',
          }}>
            Set how often you close out an accounting period. This determines auto-suggested
            report names, end dates, and warnings when a count doesn’t line up with the
            scheduled period end.
          </p>

          {/* Yes / No toggle */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.6rem',
            marginBottom: '1rem',
          }}>
            <ToggleCard
              selected={enabled}
              onClick={() => setEnabled(true)}
              icon="✅"
              title="Yes — regular schedule"
              desc="We close out on a recurring period"
            />
            <ToggleCard
              selected={!enabled}
              onClick={() => setEnabled(false)}
              icon="🚫"
              title="No regular schedule"
              desc="We count inventory as needed"
            />
          </div>

          {enabled && (
            <>
              <div style={{
                fontSize: '0.78rem',
                fontWeight: 700,
                color: '#8B7B73',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Select your accounting period schedule:
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '0.6rem',
                marginBottom: '0.85rem',
              }}>
                {SCHEDULE_OPTIONS.map((opt) => (
                  <ToggleCard
                    key={opt.value}
                    selected={scheduleType === opt.value}
                    onClick={() => setScheduleType(opt.value)}
                    icon={opt.icon}
                    title={opt.title}
                    desc={opt.desc}
                  />
                ))}
              </div>

              {scheduleType === 'custom_day' && (
                <div style={{
                  background: '#FBF6F1',
                  padding: '0.85rem 1rem',
                  borderRadius: 8,
                  border: '2px dashed #D4C4B0',
                  marginBottom: '0.85rem',
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#3D2518',
                    marginBottom: '0.4rem',
                  }}>
                    Which day of the month?
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={customDay}
                      onChange={(e) => setCustomDay(e.target.value)}
                      style={{
                        width: 90,
                        padding: '0.5rem 0.7rem',
                        border: '2px solid #D4C4B0',
                        borderRadius: 6,
                        fontSize: '0.95rem',
                        background: '#fff',
                      }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#6c625a' }}>
                      of every month (1–28)
                    </span>
                  </div>
                </div>
              )}

              {previewDates.length > 0 && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: '#EEF6EE',
                  border: '1px solid #B8E0BC',
                  borderRadius: 8,
                  marginBottom: '0.85rem',
                }}>
                  <div style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: '#2D6A31',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: '0.4rem',
                  }}>
                    Upcoming End of Period Dates
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '0.25rem 1rem',
                    fontSize: '0.88rem',
                    color: '#2D6A31',
                  }}>
                    {previewDates.map((d, i) => (
                      <li key={d}>
                        <strong>Period {i + 1}:</strong> {formatPreviewDate(d)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {error && (
            <div style={{
              padding: '0.6rem 0.85rem',
              background: '#FBE9E7',
              border: '1px solid #F0B7B0',
              borderRadius: 8,
              color: '#A03325',
              fontSize: '0.85rem',
              marginBottom: '0.85rem',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? '#9bb89c' : '#2c7a4b',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.55rem 1.15rem',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save Schedule'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              style={{
                background: '#fff',
                color: '#5A4A42',
                border: '1px solid #D4C4B0',
                borderRadius: 8,
                padding: '0.55rem 1.15rem',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ToggleCard = ({ selected, onClick, icon, title, desc }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      textAlign: 'left',
      cursor: 'pointer',
      background: selected ? '#FBF1E0' : '#fff',
      border: selected ? '2px solid #C9A66B' : '2px solid #E8DDD0',
      borderRadius: 10,
      padding: '0.75rem 0.85rem',
      display: 'flex',
      gap: '0.6rem',
      alignItems: 'flex-start',
      transition: 'border-color 120ms ease, background 120ms ease',
      width: '100%',
      font: 'inherit',
      color: 'inherit',
    }}
  >
    <span style={{ fontSize: '1.15rem', lineHeight: 1, marginTop: 1 }}>{icon}</span>
    <span style={{ display: 'block', minWidth: 0 }}>
      <span style={{
        display: 'block',
        fontWeight: 700,
        color: '#3D2518',
        fontSize: '0.9rem',
        marginBottom: 2,
      }}>
        {title}
      </span>
      <span style={{
        display: 'block',
        fontSize: '0.8rem',
        color: '#6c625a',
        lineHeight: 1.35,
      }}>
        {desc}
      </span>
    </span>
  </button>
);

export default InventoryScheduleEditor;
